import { useCallback } from 'react';
import {
  Listbox,
  Modal,
  ModalHeader,
  ModalBody,
  Heading,
  useToast,
} from '@kinesis/bungle';
import { Intervention, PathwayScenarioWithInventory } from '@/types';

const CopyIntervention = (props: CopyInterventionProps) => {
  const { intervention, onClose, scenarios, onCopy } = props;
  const toast = useToast('globalTop');

  const options = scenarios.map((s: PathwayScenarioWithInventory) => ({
    value: s.id,
    label: `${s.inventoryName} / ${s.name}`,
  }));

  const onSelect = useCallback(
    async (scenarioId: number) => {
      try {
        await onCopy(scenarioId, intervention);
        const target = scenarios.find(
          (s: PathwayScenarioWithInventory) => s.id === scenarioId,
        );
        toast(
          `Intervention copied to ${target?.inventoryName} / ${target?.name}`,
          { variant: 'success' },
        );
        onClose();
      } catch {
        toast('Could not copy intervention.', { variant: 'error' });
      }
    },
    [onCopy, intervention, scenarios, toast, onClose],
  );

  return (
    <Modal onClose={onClose}>
      <ModalHeader>
        <Heading as='h3' size='small'>
          Copy intervention to scenario
        </Heading>
      </ModalHeader>
      <ModalBody>
        <Listbox
          search
          options={options}
          labelKey='label'
          valueKey='value'
          onChange={onSelect}
          placeholder='Search scenarios...'
          emptyText='No scenarios found'
          maxHeight={300}
        />
      </ModalBody>
    </Modal>
  );
};

export type CopyInterventionProps = {
  intervention: Intervention;
  onClose: () => void;
  scenarios: PathwayScenarioWithInventory[];
  onCopy: (scenarioId: number, intervention: Intervention) => Promise<void>;
};

export { CopyIntervention };
